import { Fragment } from "react";
import { useTranslation } from "react-i18next";
import { SelectButton } from "primereact/selectbutton";

import UiButton from "../button/Button";
import ConditionRuleEditor from "./ConditionRuleEditor";
import { FieldOption } from "./ValueEditor";
import {
	BuilderNode,
	ConditionGroup,
	emptyGroup,
	emptyRule,
	isGroup,
} from "../../utils/conditions";

interface ConditionGroupEditorProps {
	group: ConditionGroup;
	onChange: (group: ConditionGroup) => void;
	/** Omitted at the root group (which can't be removed). */
	onRemove?: () => void;
	fields: FieldOption[];
	depth: number;
}

/**
 * A recursive AND/OR group. Holds any mix of rules and nested groups; the
 * AND/OR toggle, the add-rule / add-group actions, and (for non-root groups)
 * a remove button let the user assemble arbitrarily deep boolean logic.
 */
const ConditionGroupEditor = ({
	group,
	onChange,
	onRemove,
	fields,
	depth,
}: ConditionGroupEditorProps) => {
	const { t } = useTranslation("common");

	const replaceChild = (index: number, child: BuilderNode) =>
		onChange({
			...group,
			children: group.children.map((existing, i) => (i === index ? child : existing)),
		});

	const removeChild = (index: number) =>
		onChange({ ...group, children: group.children.filter((_, i) => i !== index) });

	const addRule = () =>
		onChange({ ...group, children: [...group.children, emptyRule(fields[0]?.name ?? "")] });

	const addGroup = () => onChange({ ...group, children: [...group.children, emptyGroup("and")] });

	return (
		<div
			className={`cb-group cb-group--depth-${Math.min(depth, 4)}`}
			data-kind={group.kind}
		>
			<div className="cb-group-header">
				<SelectButton
					className="cb-andor"
					value={group.kind}
					options={[
						{ label: t("MARKETING_CAMPAIGNS.CONDITIONS.AND"), value: "and" },
						{ label: t("MARKETING_CAMPAIGNS.CONDITIONS.OR"), value: "or" },
					]}
					onChange={(event) => event.value && onChange({ ...group, kind: event.value })}
				/>
				{onRemove && (
					<UiButton
						type="button"
						className="p-button-text p-button-sm neutral"
						icon="pi pi-trash"
						tooltip="MARKETING_CAMPAIGNS.CONDITIONS.REMOVE_GROUP"
						onClick={onRemove}
					/>
				)}
			</div>

			<div className="cb-group-children">
				{group.children.length === 0 && (
					<p className="cb-group-empty">
						{t("MARKETING_CAMPAIGNS.CONDITIONS.GROUP_EMPTY")}
					</p>
				)}
				{group.children.map((child, index) => (
					<Fragment key={child.id}>
						{index > 0 && (
							<div
								className="cb-connector"
								data-kind={group.kind}
							>
								<span className="cb-connector-label">
									{group.kind === "and"
										? t("MARKETING_CAMPAIGNS.CONDITIONS.AND_SHORT")
										: t("MARKETING_CAMPAIGNS.CONDITIONS.OR_SHORT")}
								</span>
							</div>
						)}
						{isGroup(child) ? (
							<ConditionGroupEditor
								group={child}
								onChange={(next) => replaceChild(index, next)}
								onRemove={() => removeChild(index)}
								fields={fields}
								depth={depth + 1}
							/>
						) : (
							<ConditionRuleEditor
								rule={child}
								onChange={(next) => replaceChild(index, next)}
								onRemove={() => removeChild(index)}
								fields={fields}
							/>
						)}
					</Fragment>
				))}
			</div>

			<div className="cb-group-actions">
				<UiButton
					type="button"
					className="p-button-text p-button-sm"
					icon="pi pi-plus"
					label="MARKETING_CAMPAIGNS.CONDITIONS.ADD_RULE"
					onClick={addRule}
				/>
				<UiButton
					type="button"
					className="p-button-text p-button-sm"
					icon="pi pi-clone"
					label="MARKETING_CAMPAIGNS.CONDITIONS.ADD_GROUP"
					onClick={addGroup}
				/>
			</div>
		</div>
	);
};

export default ConditionGroupEditor;
